import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, FileText, ListChecks, ListPlus, ListTree, PenLine, Plus, Send, Sparkles, Timer, Wand2, Zap,
} from 'lucide-react';
import BotAvatar from '../bots/BotAvatar';
import { useBotLabel } from '../bots/botDisplay';

/** What the menu asks for, grouped by the moment in the work (bots.task_prompts.<key>[_prompt]). */
export const TASK_PROMPTS = [
  { group: 'plan', items: [['breakdown', ListTree], ['checklist', ListChecks], ['next_step', Zap]] },
  { group: 'communicate', items: [['summarize', FileText], ['update', Send], ['action_items', ListPlus]] },
  { group: 'refine', items: [['polish_title', Wand2], ['write_description', PenLine]] },
  { group: 'anticipate', items: [['risks', AlertTriangle], ['estimate', Timer]] },
];

/** The bot a request goes to: the task's own bot, else the first one. */
export const defaultBot = (bots, assignedBotId) => bots.find((b) => b.id === assignedBotId) || bots[0] || null;

/**
 * The task's AI menu: asks one of the task's bots to do something with the task. The
 * request goes out as a comment that @mentions the bot (onAsk), so the bot reads the
 * task and its comments, does the work with its tools (real subtasks, a clearer title,
 * the duration) and answers in a reply; everyone on the task sees what was asked.
 * Without a bot it offers to add one.
 */
const AskBotMenu = ({ bots = [], assignedBotId, onAsk, onAddBot, disabled }) => {
  const { t } = useTranslation();
  const botLabel = useBotLabel();
  const [open, setOpen] = useState(false);
  const [chosenId, setChosenId] = useState(null);
  const bot = bots.find((b) => b.id === chosenId) || defaultBot(bots, assignedBotId);
  const ask = (key) => {
    setOpen(false);
    onAsk(bot, t(`bots.task_prompts.${key}_prompt`));
  };
  const item = 'w-full flex items-center gap-3 px-4 py-2 text-left text-sm text-foreground/80 hover:bg-muted dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(!open)} disabled={disabled} data-testid="task-ask-bot"
        title={t('bots.home.ask_bot')} aria-label={t('bots.home.ask_bot')} aria-expanded={open}
        className={`p-1.5 rounded-lg transition-colors disabled:opacity-40 ${open
          ? 'text-amber-500 bg-amber-500/10'
          : 'text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10'}`}>
        <Sparkles className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div role="menu" className="absolute right-0 top-full mt-1 w-72 bg-white dark:bg-accent border border-border rounded-lg shadow-xl z-20 py-1 max-h-[80vh] overflow-y-auto"
            data-testid="task-ask-bot-menu">
            {!bot ? (
              <button type="button" role="menuitem" className={item} onClick={() => { setOpen(false); onAddBot?.(); }}>
                <Plus className="w-4 h-4 text-muted-foreground" />
                <span>{t('bots.add_bot')}</span>
              </button>
            ) : (
              <>
                {bots.length > 1 && (
                  <div className="flex flex-wrap gap-1 px-3 pt-2 pb-2 border-b border-border" data-testid="task-ask-bot-choice">
                    {bots.map((b) => (
                      <button key={b.id} type="button" onClick={() => setChosenId(b.id)} aria-pressed={b.id === bot.id}
                        className={`inline-flex items-center gap-1.5 h-7 pl-1 pr-2 rounded-full border text-xs transition-colors ${
                          b.id === bot.id ? 'border-amber-500/60 bg-amber-500/10 text-foreground' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                        <BotAvatar bot={b} size="xs" />
                        <span className="truncate max-w-[120px]">{botLabel(b)}</span>
                      </button>
                    ))}
                  </div>
                )}
                {TASK_PROMPTS.map(({ group, items }, i) => (
                  <div key={group}>
                    <div className={`px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-amber-500/80 ${i ? 'mt-1 border-t border-border' : ''}`}>
                      {t(`bots.task_prompts.${group}`)}
                    </div>
                    {items.map(([key, Icon]) => (
                      <button key={key} type="button" role="menuitem" className={item} onClick={() => ask(key)}
                        data-testid={`task-ask-bot-${key}`}>
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span>{t(`bots.task_prompts.${key}`)}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AskBotMenu;
